import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole 
}) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  // Check if running locally
  const isLocalDevelopment = () => {
    const hostname = window.location.hostname;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '' ||
      process.env.NODE_ENV === 'development'
    );
  };

  // Check if login bypass is enabled for local development
  const shouldBypassLogin = () => {
    const bypassEnabled = process.env.REACT_APP_BYPASS_LOGIN_LOCAL === 'true';
    return isLocalDevelopment() && bypassEnabled;
  };

  const bypassLogin = shouldBypassLogin();

  // Show loading spinner while checking authentication (only if not bypassing)
  if (loading && !bypassLogin) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Authenticating...</p>
      </div>
    );
  }

  // Allow access if bypassing login locally, otherwise check authentication
  if (!bypassLogin && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if required
  const userRole = user?.role?.toLowerCase();
  const requiredRoleLower = requiredRole?.toLowerCase();
  if (requiredRoleLower && userRole !== requiredRoleLower && userRole !== 'admin') {
    return (
      <div className="error-container">
        <div className="error-message">
          <h3>Access Denied</h3>
          <p>You don't have permission to access this page.</p>
          <p>Required role: {requiredRole}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
